/**
 * Wave 4.07 — CAD Development Report.
 *
 * Lists every order whose CAD step is done, with the CAD Developer
 * attribution, CAD weight and CAD date. Filterable by Period (CAD done
 * date), CAD Developer, Page No, Order No and Customer. A result-count chip
 * (and total CAD weight) sits in the filter bar. PDF view/download is gated
 * by ``reports.cad.print``.
 *
 * Modeled after ``CustomerGoldLedgerReport`` for a native feel.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Cpu, PencilRuler, RotateCcw, CircleAlert, FileText, Download, Scale, ListChecks, Users,
  Award, TrendingDown, PieChart as PieChartIcon,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { formatDate, formatDateTime, formatGrams } from '@/lib/format';
import { cn } from '@/lib/utils';

const ALL = 'all';

// Pleasant, distinct slice colours for the contribution pie (modern shades,
// avoiding raw primaries). Unattributed CAD weight uses a muted slate.
const SLICE_COLORS = [
  '#6366f1', '#8b5cf6', '#14b8a6', '#f59e0b', '#ec4899', '#0ea5e9',
  '#10b981', '#d946ef', '#f97316', '#06b6d4', '#a855f7', '#84cc16',
];
const UNATTRIBUTED_COLOR = '#94a3b8';

// Custom hover tooltip for the contribution pie.
function PieTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="rounded-md border border-[var(--divider)] bg-white px-3 py-2 shadow-md text-xs">
      <div className="font-medium text-[var(--ink-navy)]">{p.name}</div>
      <div className="text-slate-600 font-mono tabular-nums">
        {formatGrams(p.weight)} g · {Number(p.pct || 0).toFixed(1)}%
      </div>
      <div className="text-slate-500">{p.count} CAD{p.count === 1 ? '' : 's'}</div>
    </div>
  );
}

// IST day boundaries so the ``start`` / ``end`` sent to the backend are
// timezone-aware ISO strings (the backend compares them against the
// tz-aware ``cad_done_at`` stamp). Naive date-only strings would raise a
// naive-vs-aware comparison error server-side.
const dayStartIso = (d) => (d ? `${d}T00:00:00+05:30` : undefined);
const dayEndIso = (d) => (d ? `${d}T23:59:59+05:30` : undefined);

export default function CADReport() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="CAD Development Report"
      subtitle="Every order whose CAD is developed — attributed to a CAD Developer, with CAD weight and date."
    >
      <CompanyGate>
        <ReportContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function ReportContent({ companyId }) {
  const { hasPermission } = useAuth();
  const canPrint = hasPermission('reports.cad.print');

  const [filters, setFilters] = useState({
    dateFrom: '', dateTo: '', cadDeveloperId: ALL, pageNo: '', orderNo: '', customerId: ALL,
  });
  const [options, setOptions] = useState({ developers: [], customers: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Load dropdown options once per company.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await api.get('/reports/cad-development/options', { params: { company_id: companyId } });
        if (!cancelled) setOptions({ developers: res?.developers || [], customers: res?.customers || [] });
      } catch { /* options are best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const buildParams = useCallback(() => {
    const params = { company_id: companyId };
    const start = dayStartIso(filters.dateFrom);
    const end = dayEndIso(filters.dateTo);
    if (start) params.start = start;
    if (end) params.end = end;
    if (filters.cadDeveloperId && filters.cadDeveloperId !== ALL) params.cad_developer_id = filters.cadDeveloperId;
    if (filters.customerId && filters.customerId !== ALL) params.customer_id = filters.customerId;
    const pageNo = String(filters.pageNo || '').trim();
    if (pageNo !== '' && Number.isFinite(Number(pageNo))) params.reference_no = Number(pageNo);
    const orderNo = String(filters.orderNo || '').trim();
    if (orderNo !== '') params.order_no = orderNo;
    return params;
  }, [companyId, filters]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(false);
    try {
      const { data: res } = await api.get('/reports/cad-development', { params: buildParams() });
      setData(res);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [companyId, buildParams]);

  // Debounced reload on any filter change (text inputs included).
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);

  const rows = data?.rows || [];
  const count = data?.count ?? 0;
  const totalWeight = data?.total_weight_grams ?? 0;

  // Per-developer workload for the selected period/filters. Computed from the
  // already-filtered rows so it always mirrors the table. Sorted by CAD count
  // (desc), then name; an "Unattributed" bucket collects developer-less rows.
  const workload = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.cad_developer_id || '__none__';
      const cur = map.get(key) || {
        key,
        name: r.cad_developer_name || '',
        unattributed: !r.cad_developer_id,
        count: 0,
        weight: 0,
      };
      cur.count += 1;
      cur.weight += Number(r.cad_weight_grams || 0);
      map.set(key, cur);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => (b.count - a.count) || (a.name || '').localeCompare(b.name || ''));
    return arr;
  }, [rows]);

  const [pieOpen, setPieOpen] = useState(false);

  // Named developers only (exclude the "Unattributed" bucket) — used to pick
  // the Good / Weak performer by TOTAL CAD weight in the selected period.
  const namedWorkload = useMemo(() => workload.filter((w) => !w.unattributed), [workload]);

  const { goodPerformer, weakPerformer } = useMemo(() => {
    if (namedWorkload.length === 0) return { goodPerformer: null, weakPerformer: null };
    let hi = namedWorkload[0];
    let lo = namedWorkload[0];
    for (const w of namedWorkload) {
      if (w.weight > hi.weight || (w.weight === hi.weight && w.count > hi.count)) hi = w;
      if (w.weight < lo.weight || (w.weight === lo.weight && w.count < lo.count)) lo = w;
    }
    return { goodPerformer: hi, weakPerformer: lo };
  }, [namedWorkload]);

  // Contribution pie — each developer's share of the TOTAL CAD weight produced
  // for the company in the selected period. Unattributed weight is included as
  // a muted slice so the shares are honest against the company total.
  const pie = useMemo(() => {
    const base = workload
      .filter((w) => Number(w.weight) > 0)
      .map((w) => ({
        key: w.key,
        name: w.unattributed ? 'Unattributed' : (w.name || 'Unnamed'),
        weight: Number(w.weight),
        count: w.count,
        unattributed: w.unattributed,
      }))
      .sort((a, b) => b.weight - a.weight);
    const total = base.reduce((s, x) => s + x.weight, 0);
    let ci = 0;
    const items = base.map((x) => ({
      ...x,
      pct: total > 0 ? (x.weight / total) * 100 : 0,
      color: x.unattributed ? UNATTRIBUTED_COLOR : SLICE_COLORS[ci++ % SLICE_COLORS.length],
    }));
    return { items, total };
  }, [workload]);

  const resetFilters = () => setFilters({
    dateFrom: '', dateTo: '', cadDeveloperId: ALL, pageNo: '', orderNo: '', customerId: ALL,
  });

  const periodLabel = useMemo(() => {
    const from = filters.dateFrom ? formatDate(`${filters.dateFrom}T00:00:00`) : 'Beginning';
    const to = filters.dateTo ? formatDate(`${filters.dateTo}T00:00:00`) : 'Today';
    return `${from} → ${to}`;
  }, [filters.dateFrom, filters.dateTo]);

  const runPdf = useCallback(async (mode) => {
    if (!companyId) return;
    setPdfBusy(true);
    try {
      const resp = await api.get('/reports/cad-development/pdf', { params: buildParams(), responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (mode === 'view') {
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) toast.error('Popup blocked. Allow popups to view the PDF.');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'CAD_Development_Report.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate PDF'));
    } finally {
      setPdfBusy(false);
    }
  }, [companyId, buildParams]);

  const COLS = 6;

  return (
    <div className="space-y-5" data-testid="cad-report">
      {/* Summary chips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4 border-indigo-200 bg-indigo-50/60" data-testid="cad-summary-count">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-100 text-indigo-700">
              <ListChecks size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-indigo-800/80 font-medium">CADs Developed</div>
              <div className="text-xl font-semibold text-indigo-800 font-mono tabular-nums" data-testid="cad-summary-count-value">
                {loading ? '…' : count}
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-[var(--divider)] bg-[var(--gold-bg)]" data-testid="cad-summary-weight">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/70 text-[var(--gold-icon)]">
              <Scale size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-[var(--gold-icon)]/80 font-medium">Total CAD Weight</div>
              <div className="text-xl font-semibold text-[var(--ink-navy)] font-mono tabular-nums">
                {loading ? '…' : `${formatGrams(totalWeight)} g`}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">From date</Label>
            <DatePicker
              value={filters.dateFrom}
              maxDate={filters.dateTo || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="h-9 w-44"
              data-testid="cad-filter-date-from"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">To date</Label>
            <DatePicker
              value={filters.dateTo}
              minDate={filters.dateFrom || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="h-9 w-44"
              data-testid="cad-filter-date-to"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">CAD Developer</Label>
            <Select value={filters.cadDeveloperId} onValueChange={(v) => setFilters((f) => ({ ...f, cadDeveloperId: v }))}>
              <SelectTrigger className="h-9 w-56" data-testid="cad-filter-developer">
                <SelectValue placeholder="All developers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All developers</SelectItem>
                {options.developers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Customer</Label>
            <Select value={filters.customerId} onValueChange={(v) => setFilters((f) => ({ ...f, customerId: v }))}>
              <SelectTrigger className="h-9 w-56" data-testid="cad-filter-customer">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All customers</SelectItem>
                {options.customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Page No</Label>
            <Input
              type="number"
              value={filters.pageNo}
              onChange={(e) => setFilters((f) => ({ ...f, pageNo: e.target.value }))}
              placeholder="e.g. 42"
              className="h-9 w-28"
              data-testid="cad-filter-page-no"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-600 mb-1 block">Order No</Label>
            <Input
              value={filters.orderNo}
              onChange={(e) => setFilters((f) => ({ ...f, orderNo: e.target.value }))}
              placeholder="e.g. ORD-101"
              className="h-9 w-40"
              data-testid="cad-filter-order-no"
            />
          </div>
          <Button variant="ghost" className="h-9 text-slate-500" onClick={resetFilters} data-testid="cad-filter-reset">
            <RotateCcw size={14} className="mr-1.5" /> Reset
          </Button>
        </div>

        {/* Result count + export controls */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-3 pt-3 border-t border-[var(--divider)]" data-testid="cad-controls">
          <Badge variant="outline" className="text-xs border-indigo-200 bg-indigo-50 text-indigo-700" data-testid="cad-result-count-chip">
            {loading ? 'Loading…' : `${count} CAD${count === 1 ? '' : 's'}`}
          </Badge>
          <div className="text-xs text-slate-500" data-testid="cad-period-label">
            Period: <span className="font-medium text-[var(--ink-navy)]">{periodLabel}</span>
          </div>
          {canPrint && (
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                disabled={pdfBusy || loading || error || rows.length === 0}
                onClick={() => runPdf('view')}
                data-testid="cad-view-pdf"
              >
                <FileText size={14} className="mr-1.5" /> View PDF
              </Button>
              <Button
                size="sm"
                className="h-9 luxury-button"
                disabled={pdfBusy || loading || error || rows.length === 0}
                onClick={() => runPdf('download')}
                data-testid="cad-download-pdf"
              >
                <Download size={14} className="mr-1.5" /> {pdfBusy ? 'Preparing…' : 'Download PDF'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Developer Workload — per-developer CAD counts for the current filters */}
      {!loading && !error && workload.length > 0 && (
        <Card className="p-4" data-testid="cad-workload">
          <div className="flex items-center gap-2 mb-3">
            <Users size={15} className="text-indigo-600" />
            <div className="text-sm font-medium text-[var(--ink-navy)]">Developer Workload</div>
            <span className="text-xs text-slate-500">
              · CADs produced in the selected period
              {workload.length > 1 ? ` · ${workload.length} developers` : ''}
            </span>
          </div>

          {/* Good / Weak performer + clickable contribution card */}
          {namedWorkload.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3.5 py-3" data-testid="cad-good-performer">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
                  <Award size={13} /> Good Performer
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                  <Cpu size={13} className="text-emerald-600 shrink-0" />
                  <span className="truncate" title={goodPerformer.name} data-testid="cad-good-performer-name">{goodPerformer.name}</span>
                </div>
                <div className="text-[13px] font-mono tabular-nums text-emerald-800 mt-0.5" data-testid="cad-good-performer-weight">
                  {formatGrams(goodPerformer.weight)} g
                  <span className="text-[11px] text-slate-500 ml-1.5 font-sans">· {goodPerformer.count} CAD{goodPerformer.count === 1 ? '' : 's'}</span>
                </div>
              </div>

              <div className="rounded-lg border border-rose-200 bg-rose-50/70 px-3.5 py-3" data-testid="cad-weak-performer">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-rose-600 font-semibold">
                  <TrendingDown size={13} /> Weak Performer
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-rose-600">
                  <Cpu size={13} className="text-rose-500 shrink-0" />
                  <span className="truncate" title={weakPerformer.name} data-testid="cad-weak-performer-name">{weakPerformer.name}</span>
                </div>
                <div className="text-[13px] font-mono tabular-nums text-rose-700 mt-0.5" data-testid="cad-weak-performer-weight">
                  {formatGrams(weakPerformer.weight)} g
                  <span className="text-[11px] text-slate-500 ml-1.5 font-sans">· {weakPerformer.count} CAD{weakPerformer.count === 1 ? '' : 's'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setPieOpen(true)}
                disabled={pie.total <= 0}
                className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3.5 py-3 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cad-contribution-card"
              >
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-indigo-700 font-semibold">
                  <PieChartIcon size={13} /> Contribution
                </div>
                <div className="mt-1.5 text-sm font-semibold text-[var(--ink-navy)]">View performance breakdown</div>
                <div className="text-[11px] text-indigo-700/80 mt-0.5">
                  Pie chart of each developer's share of total CAD weight →
                </div>
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cad-workload-grid">
            {workload.map((w) => (
              <div
                key={w.key}
                className="rounded-lg border border-[var(--divider)] bg-white px-3 py-2.5 flex items-center justify-between gap-2 hover:border-indigo-200 transition-colors"
                data-testid={`cad-workload-card-${w.key}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink-navy)]">
                    {w.unattributed ? (
                      <span className="italic text-slate-400">Unattributed</span>
                    ) : (
                      <>
                        <Cpu size={12} className="text-indigo-500 shrink-0" />
                        <span className="truncate" title={w.name}>{w.name}</span>
                      </>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono tabular-nums mt-0.5">
                    {formatGrams(w.weight)} g
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-semibold text-indigo-700 font-mono tabular-nums leading-none" data-testid={`cad-workload-count-${w.key}`}>
                    {w.count}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">
                    CAD{w.count === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* CAD weight contribution pie — opened from the Contribution card */}
      <Dialog open={pieOpen} onOpenChange={setPieOpen}>
        <DialogContent className="max-w-2xl" data-testid="cad-contribution-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PieChartIcon size={18} className="text-indigo-600" /> CAD Weight Contribution
            </DialogTitle>
            <DialogDescription>
              Each developer's share of the total CAD weight ({formatGrams(pie.total)} g) for {periodLabel}.
            </DialogDescription>
          </DialogHeader>
          {pie.total > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
              <div className="h-[280px]" data-testid="cad-contribution-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pie.items}
                      dataKey="weight"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={104}
                      paddingAngle={2}
                      stroke="#ffffff"
                      strokeWidth={2}
                    >
                      {pie.items.map((s) => <Cell key={s.key} fill={s.color} />)}
                    </Pie>
                    <ReTooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1" data-testid="cad-contribution-legend">
                {pie.items.map((s) => (
                  <div key={s.key} className="flex items-center justify-between gap-3 text-sm" data-testid={`cad-contribution-legend-${s.key}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
                      <span className={cn('truncate', s.unattributed && 'italic text-slate-400')} title={s.name}>{s.name}</span>
                    </div>
                    <div className="text-right shrink-0 font-mono tabular-nums">
                      <span className="text-[var(--ink-navy)] font-medium">{s.pct.toFixed(1)}%</span>
                      <span className="text-[11px] text-slate-500 ml-2">{formatGrams(s.weight)} g</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="cad-contribution-empty">
              No CAD weight recorded in this period.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ledger-table w-full text-sm" data-testid="cad-table">
            <thead>
              <tr className="text-left">
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">CAD Date</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Order No</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Page No</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">Customer</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)]">CAD Developer</th>
                <th className="px-3 py-2.5 font-semibold text-[var(--ink-navy)] text-right">CAD Weight (g)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} data-testid="cad-loading-row">
                    {Array.from({ length: COLS }).map((__, j) => (
                      <td key={j} className="px-3 py-2.5"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={COLS} className="px-3 py-16 text-center" data-testid="cad-error">
                    <CircleAlert className="mx-auto mb-3 text-rose-500" size={28} />
                    <div className="text-sm text-slate-600 mb-3">Couldn’t load the report.</div>
                    <Button variant="outline" size="sm" onClick={load}>Retry</Button>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLS} className="px-3 py-16 text-center" data-testid="cad-empty">
                    <PencilRuler className="mx-auto mb-3 text-slate-300" size={30} />
                    <div className="text-[var(--ink-navy)] font-medium">No CADs developed</div>
                    <div className="text-sm text-slate-500 mt-1">No orders match the selected filters.</div>
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.order_id} data-testid="cad-row">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDateTime(r.cad_done_at)}</td>
                    <td className="px-3 py-2 font-medium text-[var(--ink-navy)] whitespace-nowrap">{r.order_no || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 font-mono tabular-nums">{r.reference_no ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.customer_name || '—'}</td>
                    <td className="px-3 py-2">
                      {r.cad_developer_name ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-700">
                          <Cpu size={13} className="text-indigo-500" /> {r.cad_developer_name}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">Unattributed</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--ink-navy)]">
                      {r.cad_weight_grams == null ? '—' : formatGrams(r.cad_weight_grams)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-slate-400">
        CAD date is the moment the CAD production step was confirmed (shown in IST). Rows are ordered newest CAD first.
      </p>
    </div>
  );
}
